import Handlebars from "handlebars";

import { type AddressView } from "shared/mappers/database/address";

/**
 * Base class attached to the wrapper element. Every inner element gets
 * `${FORMAT_ADDRESS_CLASS_BASE}-${suffix}` (e.g. `template-util-format-address-line1`).
 */
export const FORMAT_ADDRESS_CLASS_BASE = "template-util-format-address";

type AddressLike = Partial<AddressView>;

export type FormatAddressTemplateUtilOptions = {
  /** Value of the positional argument, when one was passed: an address, or an array of them. */
  candidate?: unknown;
  /** True when a positional argument was passed, even if it resolved to null/undefined. */
  hasCandidate?: boolean;
  /** Handlebars `this`, used when no positional argument was passed. */
  context?: unknown;
  /**
   * Prefix prepended to every class, so `classPrefix="fooBar-9"` renders
   * `class="template-util-format-address-line1 fooBar-9-template-util-format-address-line1"`.
   * Build it from template data with the `concat` helper as a subexpression, e.g.
   * `classPrefix=(concat "fooBar-" this.type.id)`.
   */
  classPrefix?: string;
};

const buildClassNameFactory = (classPrefix: string) => (suffix?: string) => {
  const className = suffix
    ? `${FORMAT_ADDRESS_CLASS_BASE}-${suffix}`
    : FORMAT_ADDRESS_CLASS_BASE;
  return classPrefix ? `${className} ${classPrefix}-${className}` : className;
};

const escape = (value: unknown) =>
  Handlebars.escapeExpression(String(value ?? ""));

const readLine = (address: AddressLike, key: keyof AddressLike) => {
  const value = address[key];
  return value == undefined ? "" : String(value).trim();
};

/**
 * Renders an address as html:
 *   {{{formatAddress this}}}                  an address in hand
 *   {{{formatAddress profile.address.[0]}}}   an address by path
 *   {{{formatAddress}}}                       the current context is an address
 *   {{{formatAddress classPrefix="..."}}}     prefixes every class
 * Returns an empty string when there is nothing renderable.
 */
export const formatAddressTemplateUtil = ({
  candidate,
  hasCandidate = candidate != undefined,
  context,
  classPrefix,
}: FormatAddressTemplateUtilOptions): string => {
  try {
    // An explicitly passed but empty argument means "there is no address", so it does
    // not fall back to the context.
    const resolved = hasCandidate ? candidate : context;
    const address = (
      Array.isArray(resolved) ? resolved[0] : resolved
    ) as AddressLike;
    if (!address) {
      return "";
    }

    const classNameFor = buildClassNameFactory(classPrefix?.trim() ?? "");

    let html = "";
    (["line1", "line2", "line3"] as const).forEach((key) => {
      const line = readLine(address, key);
      if (line) {
        html += `<div class="${classNameFor(key)}">${escape(line)}</div>`;
      }
    });

    const city = readLine(address, "city");
    const state = readLine(address, "state");
    const postalCode = readLine(address, "postalCode");
    if (city || state || postalCode) {
      let cityStatePostalCode = "";
      if (city) {
        cityStatePostalCode += `<span class="${classNameFor("city")}">${escape(city)}</span>`;
      }
      if (city && state) {
        cityStatePostalCode += ", ";
      }
      if (state) {
        cityStatePostalCode += `<span class="${classNameFor("state")}">${escape(state)}</span>`;
      }
      if ((city || state) && postalCode) {
        cityStatePostalCode += " ";
      }
      if (postalCode) {
        cityStatePostalCode += `<span class="${classNameFor("postal-code")}">${escape(postalCode)}</span>`;
      }
      html += `<div class="${classNameFor("city-state-postal-code")}">${cityStatePostalCode}</div>`;
    }

    if (!html) {
      return "";
    }

    return `<div class="${classNameFor()}">${html}</div>`;
  } catch (e) {
    console.error("Error formatting address:", candidate, context, e);
    return "";
  }
};
